import { createFileRoute, notFound } from '@tanstack/react-router'
import { useState } from 'react'
import { ChevronRight, Sparkles, Wrench } from 'lucide-react'
import { MessageView } from '@/components/docfiller/Chat'
import {
  normalizeProposal,
  type ChatEntry,
} from '@/components/docfiller/chat-utils'
import type { Field, FieldProposalEvent } from '@/api/docfiller-types'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Dev-only side-by-side preview for the fill-agent chat redesign.
 *
 * Renders an IRS 8822-style fixture (raw XFA names, mixed checkbox /
 * text fields, redundant rationale) under both the OLD raw rendering
 * and the new normalised rendering — so we can capture before/after
 * screenshots without running the live SSE stream.
 *
 * Reachable only at `/dev/chat-preview` in dev builds — production
 * builds 404 the route, same as the existing AppShell preview.
 */
export const Route = createFileRoute('/dev/chat-preview')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: ChatPreview,
})

const RUN_ID = '01HF7Z9R3W9X9YQZ8K3F2EXAMPLE'

const FIELDS: Field[] = [
  field({
    uuid: 'fld-1',
    name: 'TOPMOSTSUBFORM[0].Page1[0].YourFirstName[0]',
    friendly_name: 'Your first name',
  }),
  field({
    uuid: 'fld-2',
    name: 'TOPMOSTSUBFORM[0].Page1[0].YourLastName[0]',
    friendly_name: 'Your last name',
  }),
  field({
    uuid: 'fld-3',
    name: 'TOPMOSTSUBFORM[0].Page1[0].YourSSN[0]',
    friendly_name: 'Your social security number',
  }),
  field({
    uuid: 'fld-4',
    name: 'TOPMOSTSUBFORM[0].Page1[0].Box1_TaxReturns[0]',
    friendly_name: 'Individual income tax returns (Forms 1040, 1040-SR, etc.)',
    kind: 'checkbox',
  }),
  field({
    uuid: 'fld-5',
    name: 'TOPMOSTSUBFORM[0].Page1[0].Box2_GiftEstate[0]',
    friendly_name: 'Gift, estate, or generation-skipping transfer tax returns',
    kind: 'checkbox',
  }),
  field({
    uuid: 'fld-6',
    name: 'TOPMOSTSUBFORM[0].Page1[0].OldAddress[0]',
    friendly_name: 'Old home mailing address',
  }),
  field({
    uuid: 'fld-7',
    name: 'TOPMOSTSUBFORM[0].Page1[0].NewAddress[0]',
    friendly_name: 'New home mailing address',
  }),
  field({
    uuid: 'fld-8',
    name: 'TOPMOSTSUBFORM[0].Page1[0].DaytimePhone[0]',
    friendly_name: 'Daytime telephone number',
  }),
  field({
    uuid: 'fld-9',
    name: 'TOPMOSTSUBFORM[0].Page1[0].Signature[0]',
    friendly_name: 'Your signature',
    kind: 'signature',
  }),
]

const PROPOSALS: FieldProposalEvent[] = [
  proposal('fld-1', 'Alice', 0.97, 'Pulled from the user profile.'),
  proposal('fld-2', 'Nguyen', 0.97, 'Pulled from the user profile.'),
  proposal('fld-3', '123-45-6789', 0.94, 'Read from the workspace context.'),
  proposal(
    'fld-4',
    'true',
    0.91,
    "User noted they're updating their 1040 mailing address.",
  ),
  proposal('fld-5', 'false', 0.88, 'Not mentioned in the context.'),
  proposal(
    'fld-6',
    '742 Evergreen Terrace, Springfield, OR 97477',
    0.96,
    null,
  ),
  proposal(
    'fld-7',
    '15 Mission St #3B, San Francisco, CA 94105',
    0.96,
    'Quoted directly from the user instructions.',
  ),
  proposal('fld-8', '', 0.45, 'No phone number found in the provided context.'),
  proposal('fld-9', '', 0.2, 'User has not signed the form yet.'),
]

const NORMALIZED = PROPOSALS.map((p) => normalizeProposal(p, FIELDS))

const NEW_ENTRIES: ChatEntry[] = [
  {
    kind: 'user',
    content:
      'Update my address with the IRS. Use my workspace profile for personal details and the new address from the instructions.',
  },
  { kind: 'run-start', runId: RUN_ID },
  {
    kind: 'assistant',
    content: 'Reading the form and your workspace context…',
  },
  {
    kind: 'tool',
    name: 'search_field',
    summary: 'looking up "tax_return_types"',
    parsed: { tool: 'search_field', query: 'tax_return_types' },
  },
  ...NORMALIZED.map(
    (proposal): ChatEntry => ({ kind: 'proposal', proposal }),
  ),
  { kind: 'run-done' },
]

function ChatPreview() {
  const [debug, setDebug] = useState(false)
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          Fill-agent chat — before / after
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Fixture: IRS Form 8822-style address-change form with raw XFA field
          names, mixed checkbox/text/signature fields, and the kind of
          rationale the agent emits in practice. Renders the same fixture
          under the legacy raw renderer and the redesigned normalised
          renderer so we can capture before/after screenshots.
        </p>
        <div className="flex items-center gap-2 pt-1">
          <Button
            variant={debug ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDebug((v) => !v)}
            data-testid="chat-preview-debug"
            aria-pressed={debug}
          >
            {debug ? 'Hide details' : 'Show details'} (new UI only)
          </Button>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <Pane
          tone="bad"
          title="Before — raw events"
          subtitle="Issues: raw XFA ids, true/false strings, repeated Why?, exposed run id, dumped JSON."
        >
          <BeforeTranscript />
        </Pane>

        <Pane
          tone="good"
          title="After — normalised chat"
          subtitle="Friendly names from the upload pipeline, humanised values, one rationale affordance, hidden debug telemetry."
        >
          <div className="space-y-2">
            {NEW_ENTRIES.map((entry, i) => (
              <MessageView
                key={i}
                entry={entry}
                allEntries={NEW_ENTRIES}
                debug={debug}
              />
            ))}
          </div>
        </Pane>
      </div>
    </div>
  )
}

function Pane({
  tone,
  title,
  subtitle,
  children,
}: {
  tone: 'bad' | 'good'
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <section
      className={cn(
        'rounded-lg border bg-muted/10',
        tone === 'bad'
          ? 'border-red-300/60 dark:border-red-900/60'
          : 'border-emerald-300/60 dark:border-emerald-900/60',
      )}
      data-tone={tone}
    >
      <header className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      </header>
      <div className="p-3">{children}</div>
    </section>
  )
}

/**
 * Reproduction of the legacy "raw" rendering style. Intentionally inlined
 * here (and only here) so the comparison page can render both versions
 * without keeping the old code paths alive in the production component.
 */
function BeforeTranscript() {
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary px-3 py-2 text-sm text-primary-foreground shadow-sm">
          Update my address with the IRS.
        </div>
      </div>
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          AGENT STARTED (RUN {RUN_ID.slice(0, 8)})
        </div>
      </div>
      <div className="flex justify-start">
        <details
          open
          className="max-w-[85%] rounded-2xl rounded-bl-md border bg-muted/40 px-3 py-2 text-sm shadow-sm"
        >
          <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
            <Wrench className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="font-medium">search_field</span>
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              — tax_return_types
            </span>
          </summary>
          <pre className="mt-2 max-h-72 overflow-auto rounded bg-background p-2 text-[11px] leading-relaxed text-muted-foreground">
            {JSON.stringify(
              { tool: 'search_field', query: 'tax_return_types' },
              null,
              2,
            )}
          </pre>
        </details>
      </div>
      {PROPOSALS.map((p) => (
        <RawProposalBubble key={p.field_uuid ?? p.field_name} proposal={p} />
      ))}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          DONE.
        </div>
      </div>
    </div>
  )
}

function RawProposalBubble({ proposal }: { proposal: FieldProposalEvent }) {
  const conf = `${Math.round((proposal.confidence ?? 0) * 100)}%`
  return (
    <div className="flex justify-start">
      <div
        className={cn(
          'max-w-[85%] space-y-1 rounded-2xl rounded-bl-md border px-3 py-2 text-sm shadow-sm',
          proposal.applied
            ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30'
            : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30',
        )}
      >
        <div
          className={cn(
            'flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide',
            proposal.applied
              ? 'text-emerald-700 dark:text-emerald-400'
              : 'text-amber-700 dark:text-amber-400',
          )}
        >
          <Sparkles className="size-3" />
          <span>FILLED &quot;{proposal.field_name}&quot;</span>
          <span className="ml-auto rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold dark:bg-emerald-900/50">
            {conf}
          </span>
        </div>
        <div className="break-words font-mono text-sm text-foreground">
          {proposal.value || (
            <span className="italic text-muted-foreground">(empty)</span>
          )}
        </div>
        {proposal.reasoning ? (
          <details className="text-[11px]">
            <summary className="cursor-pointer list-none hover:underline [&::-webkit-details-marker]:hidden">
              <ChevronRight className="mr-0.5 inline size-3" />
              Why?
            </summary>
            <p className="mt-1 whitespace-pre-wrap pl-4 text-xs text-muted-foreground">
              {proposal.reasoning}
            </p>
          </details>
        ) : null}
      </div>
    </div>
  )
}

function field(o: {
  uuid: string
  name: string
  friendly_name: string
  kind?: Field['kind']
}): Field {
  return {
    uuid: o.uuid,
    name: o.name,
    friendly_name: o.friendly_name,
    kind: o.kind ?? 'text',
    anchor: null,
    current_value: null,
    suggested_value: null,
    accepted_by: null,
    revision: 1,
    updated_at: new Date(0).toISOString(),
  }
}

function proposal(
  uuid: string,
  value: string,
  confidence: number,
  reasoning: string | null,
): FieldProposalEvent {
  const f = FIELDS.find((x) => x.uuid === uuid)!
  return {
    field_name: f.name,
    field_uuid: uuid,
    value,
    confidence,
    reasoning,
    run_id: RUN_ID,
    applied: true,
  }
}
